/**
 * 표준화된 에러 메시지 상수
 *
 * 일관된 사용자 경험을 위한 에러 메시지 정의입니다.
 * 모든 에러 메시지는 사용자 친화적이어야 합니다.
 *
 * @module
 * @example
 * import { ERROR_MESSAGES, getFieldErrorMessage } from '@/lib/errors/messages';
 *
 * // 사용 예시
 * if (!email) {
 *   return ERROR_MESSAGES.VALIDATION.REQUIRED('이메일');
 * }
 */

// =============================================================================
// 일반 에러 메시지
// =============================================================================

export const ERROR_MESSAGES = {
  /**
   * 폼 검증 에러
   */
  VALIDATION: {
    /** 필수 필드 에러 */
    REQUIRED: (field: string) => `${field}은(는) 필수입니다`,
    /** 최소 길이 에러 */
    MIN_LENGTH: (field: string, min: number) =>
      `${field}은(는) ${min}자 이상이어야 합니다`,
    /** 최대 길이 에러 */
    MAX_LENGTH: (field: string, max: number) =>
      `${field}은(는) ${max}자 이하여야 합니다`,
    /** 이메일 형식 에러 */
    INVALID_EMAIL: '유효한 이메일을 입력해주세요',
    /** 비밀번호 불일치 에러 */
    PASSWORD_MISMATCH: '비밀번호가 일치하지 않습니다',
    /** 숫자 형식 에러 */
    INVALID_NUMBER: (field: string) => `${field}은(는) 숫자여야 합니다`,
    /** 범위 에러 */
    OUT_OF_RANGE: (field: string, min: number, max: number) =>
      `${field}은(는) ${min}에서 ${max} 사이여야 합니다`,
  },

  /**
   * API 관련 에러
   */
  API: {
    /** 네트워크 에러 */
    NETWORK_ERROR: '네트워크 연결을 확인해주세요',
    /** 서버 에러 */
    SERVER_ERROR: '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요',
    /** 인증 에러 */
    UNAUTHORIZED: '로그인이 필요합니다',
    /** 권한 에러 */
    FORBIDDEN: '권한이 없습니다',
    /** 리소스 없음 */
    NOT_FOUND: '요청한 리소스를 찾을 수 없습니다',
    /** 타임아웃 */
    TIMEOUT: '요청 시간이 초과되었습니다. 다시 시도해주세요',
    /** 알 수 없는 에러 */
    UNKNOWN: '알 수 없는 오류가 발생했습니다',
  },

  /**
   * 플레이리스트 관련 에러
   */
  PLAYLIST: {
    /** 로딩 실패 */
    LOAD_FAILED: '플레이리스트를 불러오는데 실패했습니다',
    /** 생성 실패 */
    CREATE_FAILED: '플레이리스트 생성에 실패했습니다',
    /** 업데이트 실패 */
    UPDATE_FAILED: '플레이리스트 수정에 실패했습니다',
    /** 삭제 실패 */
    DELETE_FAILED: '플레이리스트 삭제에 실패했습니다',
    /** 음악 생성 실패 */
    GENERATE_FAILED: '음악 생성에 실패했습니다',
    /** 커버 이미지 업로드 실패 */
    COVER_UPLOAD_FAILED: '커버 이미지 업로드에 실패했습니다',
    /** YouTube 메타데이터 생성 실패 */
    YOUTUBE_METADATA_FAILED: 'YouTube 메타데이터 생성에 실패했습니다',
  },

  /**
   * 트랙 관련 에러
   */
  TRACK: {
    /** 로딩 실패 */
    LOAD_FAILED: '트랙을 불러오는데 실패했습니다',
    /** 생성 실패 */
    CREATE_FAILED: '트랙 생성에 실패했습니다',
    /** 업데이트 실패 */
    UPDATE_FAILED: '트랙 수정에 실패했습니다',
    /** 삭제 실패 */
    DELETE_FAILED: '트랙 삭제에 실패했습니다',
    /** 재생성 실패 */
    REGENERATE_FAILED: '트랙 재생성에 실패했습니다',
    /** 버전 선택 실패 */
    VERSION_SELECT_FAILED: '버전 선택에 실패했습니다',
  },

  /**
   * 인증 관련 에러
   */
  AUTH: {
    /** 로그인 실패 */
    LOGIN_FAILED: '로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요',
    /** 회원가입 실패 */
    REGISTER_FAILED: '회원가입에 실패했습니다',
    /** 로그아웃 실패 */
    LOGOUT_FAILED: '로그아웃에 실패했습니다',
    /** 세션 만료 */
    SESSION_EXPIRED: '세션이 만료되었습니다. 다시 로그인해주세요',
    /** 이미 가입된 이메일 */
    EMAIL_EXISTS: '이미 가입된 이메일입니다',
  },

  /**
   * 파일 관련 에러
   */
  FILE: {
    /** 업로드 실패 */
    UPLOAD_FAILED: '파일 업로드에 실패했습니다',
    /** 크기 초과 */
    SIZE_EXCEEDED: '파일 크기가 너무 큽니다',
    /** 형식 오류 */
    INVALID_TYPE: '지원하지 않는 파일 형식입니다',
    /** 다운로드 실패 */
    DOWNLOAD_FAILED: '파일 다운로드에 실패했습니다',
  },

  /**
   * 오디오 어레인지먼트(편곡 렌더링) 관련 에러
   */
  AUDIO_ARRANGEMENT: {
    /** 일반 렌더 실패 (백엔드 에러 원문이 없거나 매핑 실패 시 fallback) */
    RENDER_FAILED: '편곡 렌더링에 실패했습니다. 잠시 후 다시 시도해 주세요.',
    /** ffmpeg/렌더링 시간 초과 */
    TIMEOUT:
      '렌더링 시간이 초과되었습니다. 클립 수를 줄이거나 잠시 후 다시 시도해 주세요.',
    /** 디스크 공간 부족 */
    DISK_FULL:
      '서버 디스크 여유 공간이 부족합니다. 잠시 후 다시 시도해 주세요.',
    /** 메모리/OOM */
    OUT_OF_MEMORY:
      '메모리 부족으로 렌더링이 중단되었습니다. 클립 수를 줄여 다시 시도해 주세요.',
    /** 오디오 누락 (소스 버전 audio_url 없음 등) */
    NO_AUDIO:
      '재생 가능한 오디오가 없습니다. 클립에 음악 파일이 포함된 버전을 추가한 뒤 다시 시도해 주세요.',
    /** S3 업로드 실패 */
    UPLOAD_FAILED:
      '렌더링 결과 업로드에 실패했습니다. 네트워크 상태를 확인한 뒤 다시 시도해 주세요.',
    /** 소스 다운로드 실패 (원본 audio_url fetch 실패) */
    SOURCE_DOWNLOAD_FAILED:
      '원본 오디오를 가져오는 데 실패했습니다. 잠시 후 다시 시도해 주세요.',
    /** 클립 없음 / 잘못된 설정 */
    INVALID_CONFIG:
      '편곡 설정이 올바르지 않습니다. 클립과 전환 설정을 확인해 주세요.',
  },

  /**
   * 비디오 스튜디오(영상 렌더링) 관련 에러
   */
  VIDEO_STUDIO: {
    /** 일반 렌더 실패 (백엔드 에러 원문이 없거나 매핑 실패 시 fallback) */
    RENDER_FAILED: '영상 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.',
    /** 크레딧 부족 */
    INSUFFICIENT_CREDITS:
      '크레딧이 부족합니다. 결제 설정에서 크레딧을 충전한 뒤 다시 시도해 주세요.',
    /** ffmpeg/렌더링 시간 초과 */
    TIMEOUT:
      '영상 처리 시간이 초과되었습니다. 영상 길이를 줄이거나 잠시 후 다시 시도해 주세요.',
    /** 디스크 공간 부족 */
    DISK_FULL:
      '서버 디스크 여유 공간이 부족합니다. 잠시 후 다시 시도해 주세요.',
    /** 메모리/OOM */
    OUT_OF_MEMORY:
      '메모리 부족으로 렌더링이 중단되었습니다. 해상도(2K → 1080p)를 낮추거나 트랙 수를 줄여 다시 시도해 주세요.',
    /** 오디오 누락 */
    NO_AUDIO:
      '재생 가능한 오디오가 없습니다. 트랙에 음악을 먼저 생성한 뒤 다시 시도해 주세요.',
    /** S3 업로드 실패 */
    UPLOAD_FAILED:
      '영상 업로드에 실패했습니다. 네트워크 상태를 확인한 뒤 다시 시도해 주세요.',
    /** 최대 길이 초과 */
    TOO_LONG:
      '영상 길이가 최대 허용 시간을 초과합니다. 트랙 일부를 제외하고 다시 시도해 주세요.',
  },
} as const;

// =============================================================================
// 도메인별 에러 매퍼
// =============================================================================

/**
 * 비디오 렌더링 백엔드 에러 문자열을 사용자 친화 메시지로 매핑합니다.
 *
 * 백엔드의 raw error_message는 ffmpeg/celery/예외 원문이 노출되어
 * 사용자에게 적합하지 않습니다. 키워드 매칭으로 의미 있는 분류를 제공합니다.
 *
 * 매칭이 실패하면 원문을 함께 노출하지 않고 일반 fallback 메시지를 사용해
 * 내부 구현이 누설되지 않도록 합니다.
 *
 * @param rawMessage - 백엔드 `VideoRenderJob.error_message` 원문
 * @returns 사용자 친화 메시지
 */
export function mapVideoRenderError(
  rawMessage: string | null | undefined,
): string {
  if (!rawMessage) {
    return ERROR_MESSAGES.VIDEO_STUDIO.RENDER_FAILED;
  }

  const msg = rawMessage.toLowerCase();

  if (msg.includes('insufficient') || msg.includes('크레딧이 부족')) {
    return ERROR_MESSAGES.VIDEO_STUDIO.INSUFFICIENT_CREDITS;
  }
  if (
    msg.includes('timeout')
    || msg.includes('timed out')
    || msg.includes('softtimelimit')
    || msg.includes('시간이 초과')
  ) {
    return ERROR_MESSAGES.VIDEO_STUDIO.TIMEOUT;
  }
  if (msg.includes('no space') || msg.includes('disk') || msg.includes('디스크')) {
    return ERROR_MESSAGES.VIDEO_STUDIO.DISK_FULL;
  }
  if (
    msg.includes('memoryerror')
    || msg.includes('out of memory')
    || msg.includes('oom')
    || msg.includes('killed')
  ) {
    return ERROR_MESSAGES.VIDEO_STUDIO.OUT_OF_MEMORY;
  }
  if (
    msg.includes('재생 가능한 오디오')
    || msg.includes('포함된 트랙 버전이 없습니다')
    || msg.includes('no audio')
  ) {
    return ERROR_MESSAGES.VIDEO_STUDIO.NO_AUDIO;
  }
  if (
    msg.includes('upload')
    || msg.includes('s3')
    || msg.includes('업로드')
  ) {
    return ERROR_MESSAGES.VIDEO_STUDIO.UPLOAD_FAILED;
  }
  if (
    msg.includes('최대 허용 시간')
    || msg.includes('too long')
    || msg.includes('exceeds')
  ) {
    return ERROR_MESSAGES.VIDEO_STUDIO.TOO_LONG;
  }

  return ERROR_MESSAGES.VIDEO_STUDIO.RENDER_FAILED;
}

/**
 * 오디오 어레인지먼트 렌더링 백엔드 에러 문자열을 사용자 친화 메시지로 매핑합니다.
 *
 * `AudioArrangement.error_message`에는 ffmpeg/celery/예외 원문이 노출되므로
 * 키워드 매칭으로 의미 있는 분류를 제공합니다.
 *
 * 매칭이 실패하면 원문을 노출하지 않고 일반 fallback 메시지를 사용해
 * 내부 구현이 누설되지 않도록 합니다.
 *
 * @param rawMessage - 백엔드 `AudioArrangement.error_message` 원문
 * @returns 사용자 친화 메시지
 */
export function mapAudioArrangementError(
  rawMessage: string | null | undefined,
): string {
  if (!rawMessage) {
    return ERROR_MESSAGES.AUDIO_ARRANGEMENT.RENDER_FAILED;
  }

  const msg = rawMessage.toLowerCase();

  if (
    msg.includes('timeout')
    || msg.includes('timed out')
    || msg.includes('softtimelimit')
    || msg.includes('시간이 초과')
  ) {
    return ERROR_MESSAGES.AUDIO_ARRANGEMENT.TIMEOUT;
  }
  if (msg.includes('no space') || msg.includes('disk') || msg.includes('디스크')) {
    return ERROR_MESSAGES.AUDIO_ARRANGEMENT.DISK_FULL;
  }
  if (
    msg.includes('memoryerror')
    || msg.includes('out of memory')
    || msg.includes('oom')
    || msg.includes('killed')
  ) {
    return ERROR_MESSAGES.AUDIO_ARRANGEMENT.OUT_OF_MEMORY;
  }
  if (
    msg.includes('재생 가능한 오디오')
    || msg.includes('audio_url')
    || msg.includes('no audio')
    || msg.includes('오디오가 없습니다')
  ) {
    return ERROR_MESSAGES.AUDIO_ARRANGEMENT.NO_AUDIO;
  }
  if (
    msg.includes('upload')
    || msg.includes('s3')
    || msg.includes('업로드')
  ) {
    return ERROR_MESSAGES.AUDIO_ARRANGEMENT.UPLOAD_FAILED;
  }
  if (
    msg.includes('download')
    || msg.includes('fetch')
    || msg.includes('다운로드')
    || msg.includes('connectionerror')
    || msg.includes('httperror')
  ) {
    return ERROR_MESSAGES.AUDIO_ARRANGEMENT.SOURCE_DOWNLOAD_FAILED;
  }
  if (
    msg.includes('invalid')
    || msg.includes('잘못된')
    || msg.includes('클립이 없습니다')
    || msg.includes('validation')
  ) {
    return ERROR_MESSAGES.AUDIO_ARRANGEMENT.INVALID_CONFIG;
  }

  return ERROR_MESSAGES.AUDIO_ARRANGEMENT.RENDER_FAILED;
}

// =============================================================================
// 헬퍼 함수
// =============================================================================

/**
 * 에러 객체에서 사용자 친화적인 메시지 추출
 *
 * @param error - 에러 객체
 * @param fallback - 기본 메시지
 * @returns 사용자 친화적인 에러 메시지
 */
export function extractErrorMessage(
  error: unknown,
  fallback: string = ERROR_MESSAGES.API.UNKNOWN
): string {
  if (!error) {
    return fallback;
  }

  // Error 객체인 경우
  if (error instanceof Error) {
    return error.message || fallback;
  }

  // 문자열인 경우
  if (typeof error === 'string') {
    return error;
  }

  // 객체인 경우 (API 에러 응답)
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;
    if (typeof errorObj.message === 'string') {
      return errorObj.message;
    }
    if (typeof errorObj.detail === 'string') {
      return errorObj.detail;
    }
  }

  return fallback;
}

// =============================================================================
// 성공 메시지
// =============================================================================

export const SUCCESS_MESSAGES = {
  /**
   * 플레이리스트 관련 성공 메시지
   */
  PLAYLIST: {
    CREATED: '플레이리스트가 생성되었습니다',
    UPDATED: '플레이리스트가 수정되었습니다',
    DELETED: '플레이리스트가 삭제되었습니다',
    DUPLICATED: '플레이리스트가 복제되었습니다',
    GENERATION_STARTED: '음악 생성이 시작되었습니다',
    GENERATION_COMPLETED: '음악 생성이 완료되었습니다',
    COVER_UPLOADED: '커버 이미지가 업로드되었습니다',
    YOUTUBE_METADATA_GENERATED: 'YouTube 메타데이터가 생성되었습니다',
  },

  /**
   * 트랙 관련 성공 메시지
   */
  TRACK: {
    CREATED: '트랙이 생성되었습니다',
    UPDATED: '트랙이 수정되었습니다',
    DELETED: '트랙이 삭제되었습니다',
    REGENERATION_STARTED: '트랙 재생성이 시작되었습니다',
    VERSION_SELECTED: '버전이 선택되었습니다',
  },

  /**
   * 인증 관련 성공 메시지
   */
  AUTH: {
    LOGGED_IN: '로그인되었습니다',
    LOGGED_OUT: '로그아웃되었습니다',
    REGISTERED: '회원가입이 완료되었습니다',
    PASSWORD_CHANGED: '비밀번호가 변경되었습니다',
  },

  /**
   * 파일 관련 성공 메시지
   */
  FILE: {
    UPLOADED: '파일이 업로드되었습니다',
    DOWNLOADED: '파일이 다운로드되었습니다',
  },
} as const;

